import { RouterProvider, createBrowserRouter, redirect } from 'react-router-dom'

import { ProtectedRoute } from '@/workspace/lib/auth'

import { type QueryClient, useQueryClient } from '@tanstack/react-query'
import { useMemo } from 'react'
import { AppRoot } from './root'
import { V2AppRoot } from './v2-root'

const RootErrorBoundary = () => {
  return (
    <div style={{ padding: '20px', textAlign: 'center' }}>
      <h1>問題が発生しました</h1>
      <p>ページを再読み込みするか、しばらくしてから再度お試しください。</p>
    </div>
  )
}

// biome-ignore lint/suspicious/noExplicitAny: <explanation>
const convert = (queryClient: QueryClient) => (m: any) => {
  const { clientLoader, clientAction, default: Component, ...rest } = m
  return {
    ...rest,
    loader: clientLoader?.(queryClient),
    action: clientAction?.(queryClient),
    Component,
  }
}

const reloadWithCacheBusting = () => {
  const currentUrl = window.location.href.split('?')[0]
  const cacheBuster = `cache=${new Date().getTime()}`
  window.location.href = `${currentUrl}?${cacheBuster}`
}

// biome-ignore lint/suspicious/noExplicitAny: <explanation>
const errorHandle = (error: any) => {
  if (
    error.message.includes('Loading chunk') ||
    error.message.includes(
      "Cannot read properties of undefined (reading 'call')",
    )
  ) {
    reloadWithCacheBusting()
  }
  throw error
}

const createAppRouter = (queryClient: QueryClient) =>
  createBrowserRouter([
    {
      path: '/login',
      lazy: () =>
        import('./home/auth/auth-login-page')
          .then(convert(queryClient))
          .catch(errorHandle),
      ErrorBoundary: RootErrorBoundary,
    },
    {
      path: '/password/email',
      lazy: () =>
        import('./home/auth/auth-password-email-page')
          .then(convert(queryClient))
          .catch(errorHandle),
      ErrorBoundary: RootErrorBoundary,
    },
    {
      path: '/password/reset/:id',
      lazy: () =>
        import('./home/auth/auth-password-reset-page')
          .then(convert(queryClient))
          .catch(errorHandle),
      ErrorBoundary: RootErrorBoundary,
    },
    {
      path: '/invites/:id',
      lazy: () =>
        import('./home/auth/auth-sign-up-page')
          .then(convert(queryClient))
          .catch(errorHandle),
      ErrorBoundary: RootErrorBoundary,
    },
    {
      element: (
        <ProtectedRoute>
          <AppRoot />
        </ProtectedRoute>
      ),
      ErrorBoundary: RootErrorBoundary,
      children: [
        {
          path: '/home/services',
          lazy: () =>
            import('./home/services/service-list-page')
              .then(convert(queryClient))
              .catch(errorHandle),
        },
        {
          path: '/home/setting/profile/edit',
          lazy: () =>
            import('./home/profile/profile-setting-page')
              .then(convert(queryClient))
              .catch(errorHandle),
        },
        {
          path: '/home/setting/workspace/edit',
          lazy: () =>
            import('./home/workspace/workspace-setting-page')
              .then(convert(queryClient))
              .catch(errorHandle),
        },
        {
          path: '/home/setting/users',
          lazy: () =>
            import('./home/user/user-list-page')
              .then(convert(queryClient))
              .catch(errorHandle),
        },
        {
          path: '/home/setting/users/invite',
          lazy: () =>
            import('./home/user/user-list-page')
              .then(convert(queryClient))
              .catch(errorHandle),
        },
        {
          path: '/home/setting/users/:userId/edit',
          lazy: () =>
            import('./home/user/user-detail-page')
              .then(convert(queryClient))
              .catch(errorHandle),
        },
        {
          path: '/lead/leads/:id',
          lazy: async () => {
            return await import('./lead/lead/lead-detail-page')
              .then(convert(queryClient))
              .catch(errorHandle)
          },
        },
        {
          path: '/lead/action-history',
          lazy: () =>
            import('./lead/tracking/web-action-history-page')
              .then(convert(queryClient))
              .catch(errorHandle),
        },
        {
          path: '/lead/tracking',
          lazy: () =>
            import('./lead/tracking/tracking-list-page')
              .then(convert(queryClient))
              .catch(errorHandle),
        },
        {
          path: '/lead/tracking/create',
          lazy: () =>
            import('./lead/tracking/tracking-create-page')
              .then(convert(queryClient))
              .catch(errorHandle),
        },
        {
          path: '/lead/tracking/:id/edit',
          lazy: () =>
            import('./lead/tracking/tracking-edit-page')
              .then(convert(queryClient))
              .catch(errorHandle),
        },
        {
          path: '/lead/forms',
          lazy: () =>
            import('./lead/forms/form-list-page')
              .then(convert(queryClient))
              .catch(errorHandle),
        },
        {
          path: '/lead/forms/:id/edit',
          lazy: () =>
            import('./lead/forms/form-update-page')
              .then(convert(queryClient))
              .catch(errorHandle),
        },
        {
          path: '/lead/forms/create',
          lazy: () =>
            import('./lead/forms/form-create-page')
              .then(convert(queryClient))
              .catch(errorHandle),
        },
        {
          path: '/lead/documents',
          lazy: () =>
            import('./lead/document/document-list-page')
              .then(convert(queryClient))
              .catch(errorHandle),
        },
        {
          path: '/lead/documents/:id/edit',
          lazy: () =>
            import('./lead/document/document-detail-page')
              .then(convert(queryClient))
              .catch(errorHandle),
        },
        {
          path: '/lead/documents/add',
          lazy: () =>
            import('./lead/document/document-add-page')
              .then(convert(queryClient))
              .catch(errorHandle),
        },
        {
          path: '/cms/:siteName',
          lazy: () =>
            import('./cms/cms-dashboard-page')
              .then(convert(queryClient))
              .catch(errorHandle),
        },
        {
          path: '/cms/:siteName/dashboard',
          lazy: () =>
            import('./cms/cms-dashboard-page')
              .then(convert(queryClient))
              .catch(errorHandle),
        },
        {
          path: '/cms/:siteName/setting/rss/:rssChannelType/edit',
          lazy: () =>
            import('./cms/rss/rss-setting-page')
              .then(convert(queryClient))
              .catch(errorHandle),
        },
        {
          path: '/cms/:siteName/setting/site/edit',
          lazy: () =>
            import('./cms/site/site-setting-page')
              .then(convert(queryClient))
              .catch(errorHandle),
        },
        {
          path: '/cms/:siteName/setting/redirections',
          lazy: () =>
            import('./cms/redirection/redirection-list-page')
              .then(convert(queryClient))
              .catch(errorHandle),
        },
        {
          path: '/cms/:siteName/setting/redirections/create',
          lazy: () =>
            import('./cms/redirection/redirection-create-page')
              .then(convert(queryClient))
              .catch(errorHandle),
        },
        {
          path: '/cms/:siteName/setting/redirections/:redirectionId/edit',
          lazy: () =>
            import('./cms/redirection/redirection-edit-page')
              .then(convert(queryClient))
              .catch(errorHandle),
        },
        {
          path: '/cms/:siteName/setting/cookie/edit',
          lazy: () =>
            import('./cms/cookie/cookie-setting-page')
              .then(convert(queryClient))
              .catch(errorHandle),
        },
        {
          path: '/cms/:siteName/page/file-upload/file',
          lazy: () =>
            import('./cms/file-upload/file-list-page')
              .then(convert(queryClient))
              .catch(errorHandle),
        },
        {
          path: '/cms/:siteName/page/file-upload/file/create',
          lazy: () =>
            import('./cms/file-upload/file-create-page')
              .then(convert(queryClient))
              .catch(errorHandle),
        },
        {
          path: '/cms/:siteName/page/file-upload/file/:fileId/edit',
          lazy: () =>
            import('./cms/file-upload/file-edit-page')
              .then(convert(queryClient))
              .catch(errorHandle),
        },
        {
          path: '/cms/:siteName/page/file-upload/html',
          lazy: () =>
            import('./cms/file-upload/html-list-page')
              .then(convert(queryClient))
              .catch(errorHandle),
        },
        {
          path: '/cms/:siteName/page/file-upload/html/create',
          lazy: () =>
            import('./cms/file-upload/html-create-page')
              .then(convert(queryClient))
              .catch(errorHandle),
        },
        {
          path: '/cms/:siteName/page/file-upload/html/:htmlId/edit',
          lazy: () =>
            import('./cms/file-upload/html-edit-page')
              .then(convert(queryClient))
              .catch(errorHandle),
        },
      ],
    },
    {
      element: (
        <ProtectedRoute>
          <V2AppRoot />
        </ProtectedRoute>
      ),
      ErrorBoundary: RootErrorBoundary,
      children: [
        {
          path: '/',
          loader: () => redirect('/home'),
        },
        {
          path: '/home',
          lazy: () =>
            import('./home/dashboard-page')
              .then(convert(queryClient))
              .catch(errorHandle),
          handle: { value: 'home' },
        },
        {
          path: '/lead/leads',
          lazy: () =>
            import('../v2/Components/Pages/LeadListPage')
              .then(convert(queryClient))
              .catch(errorHandle),
          handle: { value: 'lead' },
        },
        {
          path: '/lead/leads/import',
          lazy: () =>
            import('../v2/Components/Pages/LeadImportPage')
              .then(convert(queryClient))
              .catch(errorHandle),
          handle: { value: 'lead' },
        },
        {
          path: '/lead/leads/add',
          lazy: () =>
            import('../v2/Components/Organisms/LeadForm')
              .then(convert(queryClient))
              .catch(errorHandle),
          handle: { value: 'lead' },
        },
        {
          path: '/lead/properties',
          lazy: () =>
            import('../v2/Components/Pages/PropertyListPage')
              .then(convert(queryClient))
              .catch(errorHandle),
          handle: { value: 'lead' },
        },
        {
          path: '/lead/properties/:id/edit',
          lazy: () =>
            import('../v2/Components/Pages/PropertyDetailPage')
              .then(convert(queryClient))
              .catch(errorHandle),
          handle: { value: 'lead' },
        },
        {
          path: '/lead/properties/add',
          lazy: () =>
            import('../v2/Components/Pages/PropertyDetailPage')
              .then(convert(queryClient))
              .catch(errorHandle),
          handle: { value: 'lead' },
        },
        {
          path: '/lead/lists',
          lazy: () =>
            import('../v2/Components/Pages/ListListPage')
              .then(convert(queryClient))
              .catch(errorHandle),
          handle: { value: 'lead' },
        },
        {
          path: '/lead/lists/add',
          lazy: () =>
            import('../v2/Components/Pages/ListForm')
              .then(convert(queryClient))
              .catch(errorHandle),
          handle: { value: 'lead' },
        },
        {
          path: '/lead/lists/:id/edit',
          lazy: () =>
            import('../v2/Components/Pages/ListDetailPage')
              .then(convert(queryClient))
              .catch(errorHandle),
          handle: { value: 'lead' },
        },
        {
          path: '/cms/:site_name/posts/:post_type_name/add',
          lazy: () =>
            import('./cms/post/post-create-page')
              .then(convert(queryClient))
              .catch(errorHandle),
          handle: { value: 'cms' },
        },
        {
          path: '/cms/:site_name/posts/:post_type_name/:post_id/edit',
          lazy: () =>
            import('./cms/post/post-edit-page')
              .then(convert(queryClient))
              .catch(errorHandle),
          handle: { value: 'cms' },
        },
        {
          path: '/cms/:site_name/posts/:post_type_name/:filter',
          lazy: () =>
            import('../v2/Components/Pages/PostListPage')
              .then(convert(queryClient))
              .catch(errorHandle),
          handle: { value: 'cms' },
        },
        {
          path: '/cms/:site_name/posts/:post_type_name',
          lazy: () =>
            import('../v2/Components/Pages/PostListPage')
              .then(convert(queryClient))
              .catch(errorHandle),
          handle: { value: 'cms' },
        },
        {
          path: '/cms/:site_name/posts/create',
          lazy: () =>
            import('../v2/Components/Pages/PostListPage')
              .then(convert(queryClient))
              .catch(errorHandle),
          handle: { value: 'cms' },
        },
        {
          path: '/cms/:site_name/posts/:post_type_name/taxonomies/:taxonomy_slug/add',
          lazy: () =>
            import('../v2/Components/Pages/TaxonomyListPage')
              .then(convert(queryClient))
              .catch(errorHandle),
          handle: { value: 'cms' },
        },
        {
          path: '/cms/:site_name/posts/:post_type_name/taxonomies/:taxonomy_slug/:id/edit',
          lazy: () =>
            import('../v2/Components/Pages/TaxonomyListPage')
              .then(convert(queryClient))
              .catch(errorHandle),
          handle: { value: 'cms' },
        },
        {
          path: '/cms/:site_name/posts/:post_type_name/taxonomies/:taxonomy_slug',
          lazy: () =>
            import('../v2/Components/Pages/TaxonomyListPage')
              .then(convert(queryClient))
              .catch(errorHandle),
          handle: { value: 'cms' },
        },
        {
          path: '/cms/:site_name/posts/:post_type_name/post_types/:id/edit',
          lazy: () =>
            import('../v2/Components/Pages/PostTypeDetailPage')
              .then(convert(queryClient))
              .catch(errorHandle),
          handle: { value: 'cms' },
        },
        {
          path: '/cms/:site_name/page/static-pages/add',
          lazy: () =>
            import('../v2/Components/Pages/StaticPageListPage')
              .then(convert(queryClient))
              .catch(errorHandle),
          handle: { value: 'cms' },
        },
        {
          path: '/cms/:site_name/page/static-pages/:id/edit',
          lazy: () =>
            import('../v2/Components/Pages/StaticPageListPage')
              .then(convert(queryClient))
              .catch(errorHandle),
          handle: { value: 'cms' },
        },
        {
          path: '/cms/:site_name/page/404',
          lazy: () =>
            import('../v2/Components/Pages/StaticPageListPage')
              .then(convert(queryClient))
              .catch(errorHandle),
          handle: { value: 'cms' },
        },
        {
          path: '/cms/:site_name/page/static-pages',
          lazy: () =>
            import('../v2/Components/Pages/StaticPageListPage')
              .then(convert(queryClient))
              .catch(errorHandle),
          handle: { value: 'cms' },
        },
        {
          path: '/cms/:site_name/page/dynamic-pages/:id/edit',
          lazy: () =>
            import('../v2/Components/Pages/DynamicPageDetailPage')
              .then(convert(queryClient))
              .catch(errorHandle),
          handle: { value: 'cms' },
        },
        {
          path: '/cms/:site_name/page/dynamic-pages',
          lazy: () =>
            import('../v2/Components/Pages/DynamicPageListPage')
              .then(convert(queryClient))
              .catch(errorHandle),
          handle: { value: 'cms' },
        },
      ],
    },
    {
      path: '*',
      element: <NotFoundRedirect />,
    },
  ])

const NotFoundRedirect = () => {
  if (window.history.length > 1) {
    window.history.back()
  } else {
    window.location.replace('/home')
  }

  return null
}

export const AppRouter = () => {
  const queryClient = useQueryClient()

  const router = useMemo(() => createAppRouter(queryClient), [queryClient])

  return <RouterProvider router={router} />
}
